import { IIncidentDocumentPaneProps } from "./IncidentDocumentPane";
import { DocumentUploadViewModel, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useState } from "react";
import { useValidator } from "@common/hooks/validation";
import { put, sendWithFile } from "@common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { createPlaceholderFile, getFileToUpload } from "@common/documentUploadFile";
import { isNullOrWhitespace } from "@common/stringOperations";
import { IncidentDocumentSaveModel, IncidentDocumentViewModel } from "../IncidentDocument.types";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { usePane } from "../../../../main/panes/paneLayout/usePane";
import { usePanes } from "../../../../main/panes/usePanes";

export function useIncidentDocumentPane(props: IIncidentDocumentPaneProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { close, setHasUnsavedChanges } = usePane();
    const { refetchPageBehindPanes } = usePanes();
    const isEdit = props.selectedDocument !== undefined;

    const [document, setDocument] = useState<IncidentDocumentViewModel>(new IncidentDocumentViewModel(props.selectedDocument?.name));
    const [documentUploadModel, setDocumentUploadModel] = useState<DocumentUploadViewModel>(
        new DocumentUploadViewModel(isEdit && !props.selectedDocument?.link ? [createPlaceholderFile()] : [], props.selectedDocument?.link)
    );

    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const validation = useValidator<IncidentDocumentViewModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        []
    );

    const saveMutation = useDotLegalMutation(saveDocument, {
        onSuccess: () => {
            snackbar.show(translateString(isEdit ? "documentWasUpdated" : "documentCreated"));
            refetchPageBehindPanes();
            close();
        },
    });

    function saveDocument(saveModel: IncidentDocumentSaveModel) {
        if (isEdit) {
            return put<string>(`/incidentLogEntry/${props.selectedDocument?.documentId}/Document`, saveModel);
        }
        return sendWithFile<string>(
            "POST",
            `/incidentLogEntry/${props.incidentId}/Document`,
            saveModel,
            getFileToUpload(documentUploadModel.files),
            trackUploadProgress()
        );
    }

    const save = async () => {
        validation.setShowErrors(true);

        if (validation.isValid && documentUploadModel.IsModelValid()) {
            await saveMutation.mutateAsync(mapToSaveModel(document.name, documentUploadModel));
        }
    };

    const modelUpdater = {
        onNameChange: (name: string) => {
            setHasUnsavedChanges(true);
            setDocument({ ...document, name });
        },
        onDocumentUploadModelChange: (model: DocumentUploadViewModel) => {
            setHasUnsavedChanges(true);
            setDocumentUploadModel(model);
            setDocument({ ...document, name: getNameDefaultedFromFile(document.name, model) });
        },
    };

    return {
        document,
        save,
        validation,
        isEdit,
        modelUpdater,
        isSaving: saveMutation.isLoading,
        isUploading: saveMutation.isLoading && getFileToUpload(documentUploadModel.files) !== undefined,
        uploadPercentage,
        documentUploadModel,
        // A file can't be replaced on edit, only renamed; a link can be changed.
        showUpload: !isEdit || !!documentUploadModel.link,
    };
}

function getNameDefaultedFromFile(currentName: string | undefined, model: DocumentUploadViewModel) {
    if (!isNullOrWhitespace(currentName)) {
        return currentName;
    }
    const fileName = model.files && model.files.length > 0 ? model.files[0].fileName : "";
    return fileName.substring(0, fileName.lastIndexOf("."));
}

export const mapToSaveModel = (documentName: string | undefined, documentUploadModel: DocumentUploadViewModel) => {
    const hasFiles = documentUploadModel.files && documentUploadModel.files.length;
    const model: IncidentDocumentSaveModel = {
        fileName: hasFiles ? documentUploadModel.files[0].fileName : "",
        name: documentName ?? "",
        link: documentUploadModel.link,
    };

    return model;
};
